import React, { useState } from "react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

function PlateSvg({ text, vented }) {
  const raw = (text.trim() || "YOUR TEXT").toUpperCase().slice(0, 14);
  const numberPart = (raw.match(/\d+/g) || []).join("").slice(0, 6);
  const textPart = raw.replace(/\d+/g, " ").replace(/\s+/g, " ").trim();
  const hasNumber = numberPart.length > 0;
  const hasText = textPart.length > 0;
  // default placeholder sits on the upper band like a name
  const bandLabel = hasText ? textPart : hasNumber ? "" : raw;
  // Surron front number plate: concave dipped top, flared shoulders, mesh grille
  // split by a solid center bar, tapered chin — matches the reference model
  const outer = "M115 70 Q240 108 365 70 L400 170 L368 278 L296 252 L240 288 L184 252 L112 278 L80 170 Z";
  // 1 digit: narrow center bar, big number. 2+ digits: wide deck, smaller vents
  const wide = numberPart.length >= 2;
  const grilleL = wide ? "M118 162 L186 162 L182 242 L126 250 Z" : "M118 162 L206 162 L206 242 L126 250 Z";
  const grilleR = wide ? "M294 162 L362 162 L354 250 L298 242 Z" : "M274 162 L362 162 L354 250 L274 242 Z";
  const centerBar = wide ? "M190 148 L290 148 L284 258 L196 258 Z" : "M212 150 L268 150 L262 258 L218 258 Z";
  // crosshatch mesh lines
  const mesh = [];
  for (let i = -20; i < 40; i++) {
    mesh.push(<line key={`a${i}`} x1={i * 14} y1={140} x2={i * 14 + 130} y2={270} stroke="#525252" strokeWidth={1.4} />);
    mesh.push(<line key={`b${i}`} x1={i * 14 + 130} y1={140} x2={i * 14} y2={270} stroke="#525252" strokeWidth={1.4} />);
  }
  return (
    <svg
      data-testid="plate-preview-svg"
      viewBox="0 0 480 320"
      className="w-full h-auto rounded border border-neutral-800 bg-neutral-950"
    >
      <defs>
        <linearGradient id="plateSheen" x1="0" y1="0" x2="0.8" y2="1">
          <stop offset="0%" stopColor="#2e2e2e" />
          <stop offset="45%" stopColor="#141414" />
          <stop offset="100%" stopColor="#202020" />
        </linearGradient>
        <linearGradient id="barSheen" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#262626" />
          <stop offset="50%" stopColor="#101010" />
          <stop offset="100%" stopColor="#2a2a2a" />
        </linearGradient>
        <clipPath id="grilleClipL"><path d={grilleL} /></clipPath>
        <clipPath id="grilleClipR"><path d={grilleR} /></clipPath>
      </defs>

      {/* fork mount hints behind plate */}
      <rect x="150" y="270" width="24" height="44" rx="6" fill="#0a0a0a" stroke="#262626" />
      <rect x="306" y="270" width="24" height="44" rx="6" fill="#0a0a0a" stroke="#262626" />

      {/* plate body */}
      <path d={outer} fill="url(#plateSheen)" stroke="#4a4a4a" strokeWidth={2} />

      {/* grille openings */}
      {vented ? (
        <>
          <path d={grilleL} fill="#050505" stroke="#3a3a3a" strokeWidth={1.5} />
          <path d={grilleR} fill="#050505" stroke="#3a3a3a" strokeWidth={1.5} />
          <g clipPath="url(#grilleClipL)">{mesh}</g>
          <g clipPath="url(#grilleClipR)">{mesh}</g>
        </>
      ) : (
        <>
          <path d={grilleL} fill="#191919" stroke="#333333" strokeWidth={1.5} />
          <path d={grilleR} fill="#191919" stroke="#333333" strokeWidth={1.5} />
        </>
      )}

      {/* solid center bar */}
      <path d={centerBar} fill="url(#barSheen)" stroke="#3f3f46" strokeWidth={1.5} />

      {/* text stays on the upper band, numbers go big in the middle — both can show at once */}
      {bandLabel && (
        <text
          x="240"
          y="118"
          textAnchor="middle"
          fill="#ffffff"
          fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
          fontWeight="800"
          fontSize={bandLabel.length > 8 ? 26 : 34}
          letterSpacing="3"
        >
          {bandLabel}
        </text>
      )}
      {hasNumber && (
        <text
          x="240"
          y="232"
          textAnchor="middle"
          fill="#ffffff"
          stroke="#0a0a0a"
          strokeWidth={2}
          paintOrder="stroke"
          fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
          fontWeight="800"
          fontSize={numberPart.length >= 4 ? 48 : numberPart.length === 3 ? 58 : numberPart.length === 2 ? 72 : 96}
          letterSpacing="0"
        >
          {numberPart}
        </text>
      )}

      {/* chin mark */}
      <text x="240" y="270" textAnchor="middle" fill="#737373" fontFamily="ui-monospace, monospace" fontSize="11" letterSpacing="4">
        GRIDLINE
      </text>

      {/* bolts */}
      <circle cx="125" cy="92" r="6" fill="#0a0a0a" stroke="#525252" strokeWidth={1.5} />
      <circle cx="355" cy="92" r="6" fill="#0a0a0a" stroke="#525252" strokeWidth={1.5} />
      <circle cx="102" cy="196" r="6" fill="#0a0a0a" stroke="#525252" strokeWidth={1.5} />
      <circle cx="378" cy="196" r="6" fill="#0a0a0a" stroke="#525252" strokeWidth={1.5} />
      <circle cx="152" cy="258" r="6" fill="#0a0a0a" stroke="#525252" strokeWidth={1.5} />
      <circle cx="328" cy="258" r="6" fill="#0a0a0a" stroke="#525252" strokeWidth={1.5} />
    </svg>
  );
}

export default function PlatePreviewer() {
  const { addItem, setOrderNote, setDrawerOpen, ct } = useCart();
  const [text, setText] = useState("");
  const [vented, setVented] = useState(true);

  const addDesign = () => {
    const productId = vented ? "plate_vented" : "plate_custom";
    const note = `Plate design: "${(text.trim() || "YOUR TEXT").toUpperCase()}" (${vented ? "vented" : "solid custom"})`;
    setOrderNote((prev) => (prev ? `${prev} | ${note}` : note));
    addItem(productId);
    toast.success("Design added to cart", {
      description: note,
      action: { label: "View Cart", onClick: () => setDrawerOpen(true) },
    });
  };

  return (
    <section id="previewer" className="py-24 px-6 border-b border-neutral-800 grid-pattern">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-xs uppercase tracking-widest text-white font-bold">{ct("previewer.overline", "Live Preview")}</span>
          <h2 className="font-disp text-2xl sm:text-3xl font-extrabold uppercase text-neutral-50 mt-2">
            {ct("previewer.heading", "See Your Cut Before You Pay")}
          </h2>
          <p className="text-sm text-neutral-300 mt-4 leading-relaxed max-w-md">
            {ct("previewer.body", "Type your race number, name, or tag and watch it land on the plate. Pick solid or vented — we mill exactly what you see.")}
          </p>
          <div className="mt-8 space-y-4 max-w-md">
            <div>
              <label htmlFor="preview-text" className="block text-xs font-mono uppercase text-neutral-300 mb-1">
                Plate Text / Race Number
              </label>
              <input
                id="preview-text"
                data-testid="previewer-text-input"
                type="text"
                maxLength={14}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="e.g. 77 or GHOST"
                className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm text-neutral-100 focus:border-white focus:outline-none font-mono uppercase tracking-widest"
              />
            </div>
            <div className="flex gap-3">
              <button
                data-testid="previewer-vented-toggle"
                onClick={() => setVented(true)}
                className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider rounded transition-colors ${
                  vented ? "bg-white text-neutral-950" : "bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                }`}
              >
                Vented Airflow
              </button>
              <button
                data-testid="previewer-solid-toggle"
                onClick={() => setVented(false)}
                className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider rounded transition-colors ${
                  !vented ? "bg-white text-neutral-950" : "bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                }`}
              >
                Solid Custom
              </button>
            </div>
            <button
              data-testid="previewer-add-button"
              onClick={addDesign}
              className="w-full py-4 bg-white hover:bg-neutral-200 text-neutral-950 font-bold uppercase tracking-wider text-sm rounded shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-colors"
            >
              Add This Design to Cart
            </button>
          </div>
        </div>
        <div className="relative">
          <PlateSvg text={text} vented={vented} />
          <div className="absolute -bottom-3 left-6 text-[10px] font-mono uppercase tracking-widest text-neutral-500">
            Preview is indicative — final cut is CNC-exact
          </div>
        </div>
      </div>
    </section>
  );
}
